/**
 * update-notifier.
 *
 * Listens to project notifications from server (ProjectController.notifyClients()).
 *
 * Listeners can be added on state configuration (e.g. project.config.js)
 *
 * notifications: [
 *   {
 *     room: '/project/:id',  // Listens to all changes of current project
 *   }, {
 *     room: '/project/:id',
 *     includes: {
 *       path: []             // Without a path (e.g. doesn't fire when path: [['element']])
 *     }
 *   }, {
 *     room: '/project/:id',
 *     includes: {
 *       path: [['element']]  // Only fires if path contains element
 *     }
 *   }
 * ]
 *
 * The structure of the event looks like this (adding a cluster)
 * {
 *   user_id: 37,
 *   timestamp: 1506588489492,
 *   room: '/project/67',
 *   projectEvent: 'project:changed',
 *   path: [ [ 'building', '59cbbd5e7d31acf5180bf797' ], [ 'cluster' ] ],
 *   detail: 'list_changed'
 * }
 */

import * as _ from 'lodash-es';

(function () {

  'use strict';

  angular.module('specsguardApp')
    .factory('updateNotifier', updateNotifier);

  updateNotifier.$inject = ['$rootScope', '$transitions', 'SocketService',
    'Auth', 'errorMessageService', 'ProjectDataservice'];

  function updateNotifier ($rootScope, $transitions, SocketService,
    Auth, errorMessageService, ProjectDataservice) {

    let rooms: any = [];
    let connected = false;
    let listeners = [];

    resetListeners();

    function _ensureAuthentication (cb, trans) {
      const isLoggedIn = Auth.isLoggedIn();

      if (isLoggedIn) {
        if (!SocketService.authenticated) {
          SocketService.connect(Auth.getToken(), (socket) => {
            addSocketEventListeners(socket);
            cb(trans);
          });
        } else {
          cb(trans);
        }
      }
    };

    function notificationFromCurrentUser (event) {
      return event.user_id === Auth.getCurrentUser()._id;
    }

    // checks each listener and shows notification if one listener matches
    function _onNotification (event) {
      if (notificationFromCurrentUser(event)) {
        return;
      }

      if (event.projectEvent === 'projectlist:changed') {
        ProjectDataservice.resetProjectList();
      } else if (event.projectEvent === 'project:changed') {
        const projectId = event.room.split('/')[2];
        ProjectDataservice.resetProjectById(projectId);
      }

      // always broadcast event so it can be handled locally.
      broadcast(event);

      for (const listener of listeners) {
        if (event.room === listener.room && !listener.generic) {
          const includes = listener.includes || {};
          const includesOk = Object.keys(includes).map((key) => {
            const eventValues = event[key] || [];
            const listenerIncludes = includes[key];

            // require no value
            if (listenerIncludes.length === 0 && eventValues.length === 0) {
              return true;
            }

            // check every include constraint in order
            const result = listenerIncludes.map((listenerInclude, i) => {
              const eventValue = eventValues[i];

              if (listenerInclude.length === 1) {
                return listenerInclude[0] === (eventValue && eventValue[0]);
              }

              const keyMatches = listenerInclude[0] === (eventValue && eventValue[0]);
              const valueMatches = listenerInclude[1] === (eventValue && eventValue[1]);

              return keyMatches && valueMatches;
            });

            if (result.length === 0) {
              return false;
            }

            return result.every((x) => x === true);
          });

          const fire = includesOk.every((x) => x === true);

          if (fire) {
            const actions = listener.action || [];
            if (!actions.includes('suppressNotification')) {
              showNotification(event);
            }

            break;
          }
        }
      }
    };

    function showNotification (event) {
      switch (event.projectEvent) {
        case 'projectlist:changed':
          errorMessageService.showWarning('projectlistUpdated', false, false);
          break;

        default:
          const path = event.path && event.path[0] && event.path[0][0];

          if (path === 'element') {
            if (event.detail === 'resource_changed') {
              errorMessageService.showWarning('specificationUpdated', false, false);
            } else {
              errorMessageService.showWarning('elementlistUpdated', false, false);
            }
          } else if (path === 'module') {
            if (event.detail === 'resource_changed') {
              errorMessageService.showWarning('moduleUpdated', false, false);
            } else {
              errorMessageService.showWarning('modulelistUpdated', false, false);
            }
          } else if (path === 'basicarea') {
            if (event.detail === 'resource_changed') {
              errorMessageService.showWarning('areatemplateUpdated', false, false);
            } else {
              errorMessageService.showWarning('areatemplatelistupdated', false, false);
            }
          } else if (path === 'building' && !event.path[1]) {
            if (event.detail === 'resource_changed') {
              errorMessageService.showWarning('buildingUpdated', false, false);
            } else {
              errorMessageService.showWarning('buildinglistUpdated', false, false);
            }
          } else if (path === 'building' && event.path[1] && event.path[1][0] === 'cluster') {
            if (event.detail === 'resource_changed') {
              errorMessageService.showWarning('clusterUpdated', false, false);
            } else {
              errorMessageService.showWarning('clusterlistUpdated', false, false);
            }
          } else if (path === 'building' && event.path[1] && event.path[1][0] === 'area') {
            if (event.detail === 'resource_changed') {
              errorMessageService.showWarning('areaUpdated', false, false);
            } else {
              errorMessageService.showWarning('arealistUpdated', false, false);
            }
          } else {
            errorMessageService.showWarning('projectUpdated', false, false);
          }
      }

    }

    function broadcast (event) {
      if (event.room.includes('all')) {
        $rootScope.$broadcast('projectSocket:projectListUpdated', event);
      } else {
        const projectId = event.room.split('/')[2];
        $rootScope.$broadcast('projectSocket:projectUpdated', projectId, event);
      }
    }

    function addSocketEventListeners (socket) {
      if (!connected) {
        connected = true;

        socket
          .on('projectlist:changed', (event) => {
            _onNotification(event);
          })
          .on('project:changed', (event) => {
            _onNotification(event);
          });
      }
    };

    function resetListeners () {
      listeners = [];
    }

    function _disconnectAll () {
      listeners.forEach( (listener) => {
        SocketService.disconnectFromRoom(listener.room);
        rooms = rooms.filter((x) => x !== listener.room);
      });

      resetListeners();
    }

    function _addListener (listener) {
      if (!rooms.includes(listener.room)) {
        SocketService.connectToRoom(listener.room);
        rooms.push(listener.room);
      }

      listeners.push(listener);
    }

    function _handleTransition (trans) {
      _disconnectAll();

      const to = trans.to();
      const params = trans.params();
      let listenersOnPage = [];

      if (to.notifications) {
        listenersOnPage = _.cloneDeep(to.notifications);
      }

      listenersOnPage.forEach( (listener) => {
        if (listener.room === '/project/:id') {
          listener.room = `/project/${params.project_id}`;
        }

        // replaces valiables (e.g. {building_id}) with that building_id param
        const path = listener.includes && listener.includes.path;
        if (path) {
          listener.includes.path = path.map((tuple) => {
            const key = /{(.*)}/.exec(tuple[1]);
            const value = key && key[1] && params[key[1]];

            if (value) {
              return [tuple[0], value];
            }

            return [tuple[0]];
          });
        }

        _addListener(listener);
      });

      // Generic listeners add the ability to proxy events even though there is
      // no matching listener configured
      _addListener({room: '/project/all', generic: true});
      if (params.project_id) {
        _addListener({room: `/project/${params.project_id}`, generic: true});
      }
    }

    return {
      init: function init () {
        $transitions.onStart({}, function (trans) {
          _ensureAuthentication(_handleTransition, trans);
        });

        $rootScope.$on('loggedOut', () => {
          connected = false;
        });
      },
    };
  };

})();
